import { Outlet, useLocation } from "react-router-dom"

import { PageHeader } from "@/components/PageHeader"
import { useRbac } from "@/lib/auth"
import { puedeVerDiferencias } from "@/lib/secciones"
import { Toolbar, ToolbarField } from "@/components/Toolbar"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { GRANULARIDADES } from "@/lib/periodo"
import type { Granularidad } from "@/lib/periodo"
import { cn } from "@/lib/utils"

import {
  ANIOS_DISPONIBLES,
  FinanceFiltersProvider,
  useFinanceFilters,
} from "@/routes/finance/context"
import { nombrePais } from "@/routes/finance/mapping"
import { SeccionTabs } from "@/components/SeccionTabs"
import { ComentariosDeLaVista } from "@/components/Comentarios"
import { t } from "@/lib/i18n"

const VISTAS = [
  { to: "pnl", label: "P&L" },
  { to: "caja", label: "Flujo de caja" },
  { to: "forecast", label: "Forecast" },
  /*
    VA JUNTO AL FORECAST Y DESPUÉS, no al final de la lista. Son la misma
    pregunta contestada con dos fuentes: «Forecast» proyecta la hoja que
    importaste y «De dónde sale» lo construye desde tus contratos, tu planilla y
    lo que declaraste. Separarlas por cinco pestañas haría que nadie las
    comparara, que es justo lo que hay que hacer con ellas.
  */
  /*
      «CÓMO SE ARMA» Y NO «DE DÓNDE SALE». Cesar, punto 6 de la cuarta tanda:
      «modifiquemos el título para que quede más claro que es armado de forecast».
      «De dónde sale» se lee como una nota al pie —de dónde salió este número— y
      lo que hay detrás es la construcción entera del forecast por capas.
    */
    { to: "declarado", label: "Cómo se arma el forecast" },
  /*
    UNA PESTAÑA Y NO CUATRO. Revenue Build Up, Costo de ventas, Gastos y Payroll
    eran la misma pantalla sobre otra cuenta; la cuenta se elige dentro. Ver
    `DesgloseDeCuentasPage`.
  */
  { to: "desglose", label: "Desglose" },
  /*
    La conciliacion solo tiene sentido para quien ve LAS DOS mitades del
    sistema: a quien solo ve finanzas, comparar contra facturacion le muestra
    la mitad de una resta. La regla vive en `lib/secciones.ts` junto a las
    demas, no aca.
  */
  /*
    COBRANZAS va después de Payroll y antes de Conciliación: las cinco primeras
    son el RESULTADO —qué entró, qué costó, qué quedó— y ésta es lo que hay que
    hacer con lo que todavía no entró. `sinFiltros`: aquí no se mira un período
    sino un calendario, y la barra de año y granularidad no aplicaría nada.
  */
  /*
    FORECAST va justo después del flujo de caja y no al final: la pregunta que
    contesta —«¿y si vendo un 20% menos, me alcanza?»— se hace mirando la caja,
    no después de repasar payroll. Lleva los filtros normales porque proyecta un
    AÑO y un país, igual que el P&L.
  */
  { to: "cobranzas", label: "Cobranzas", sinFiltros: true },
  /*
    UNA SOLA PESTAÑA PARA LOS DOS CUADRES. Cesar, punto 8 de la cuarta tanda:
    «¿conciliación no es lo mismo que finanzas vs contabilidad? Si son lo mismo
    unifica esas dos hojas».

    No son lo mismo —una compara la hoja contra lo FACTURADO y la otra contra el
    LIBRO— así que no se funden los datos: se funde el SITIO, y cada mitad dice
    contra qué cuadra. Ver `CuadresPage`. Los dos nombres anteriores eran el
    problema: ninguno nombraba su otro lado.

    Sigue siendo `soloAmbasMitades`: comparar contra el libro o contra la
    facturación sólo le sirve a quien ve las dos mitades del sistema.
  */
  { to: "cuadres", label: "Cuadres", soloAmbasMitades: true },
  // Al final: es configuracion, no una vista del modelo. Pero es la pestaña de
  // la que dependen las demas —sin tipos de cambio el consolidado no existe— y
  // por eso vive en Finanzas y no escondida en Maestros.
  //
  // `sinFiltros`: aqui no se mira un periodo, se cargan los datos de base. La
  // barra de año, pais y granularidad se esconde en vez de quedarse puesta sin
  // hacer nada, que es un filtro que aparenta aplicar y no aplica.
  { to: "datos", label: "Bancos y tipos de cambio", sinFiltros: true },
  /*
    «QUÉ VA A FINANZAS», con las palabras de Cesar: «poder seleccionar del
    maestro qué cosas van a finanzas y qué cosas van a contabilidad, y del libro
    igual». Es configuración, como la de al lado, pero LLEVA FILTROS: el puente
    y los descuadres son de un año y una sociedad.

    `soloAmbasMitades`: decide entre el libro y la hoja, y eso sólo lo puede
    juzgar quien ve las dos mitades del sistema — igual que «Cuadres».
  */
  { to: "que-va-a-finanzas", label: "Qué va a Finanzas", soloAmbasMitades: true },
]

/** Las pestañas que NO son una vista del modelo y por eso no llevan filtros. */
const SIN_FILTROS = VISTAS.filter((v) => v.sinFiltros).map((v) => v.to)

function Tabs() {
  const { roles } = useRbac()
  const ambas = puedeVerDiferencias(roles)
  const vistas = VISTAS.filter((v) => !v.soloAmbasMitades || ambas)
  const { anio } = useFinanceFilters()
  return (
    <SeccionTabs tabs={vistas} ariaLabel="Vistas financieras"
                 derecha={<ComentariosDeLaVista seccion="Finanzas" vistas={vistas} anio={anio} />} />
  )
}

/**
 * Control segmentado para la granularidad, no un desplegable.
 *
 * Cuatro opciones que se cambian constantemente mientras se lee: esconderlas en
 * un `select` cuesta dos clics cada vez. Mismo criterio que el selector de libro
 * en la vista contable.
 */
function GranularidadSwitch() {
  const { granularidad, setGranularidad } = useFinanceFilters()
  return (
    <div className="space-y-1">
      <Label id="granularidad-label">{t("Período")}</Label>
      <div
        role="radiogroup"
        aria-labelledby="granularidad-label"
        className="inline-flex items-center gap-0.5 rounded-md border bg-surface p-0.5"
      >
        {GRANULARIDADES.map((g) => {
          const activo = granularidad === g.id
          return (
            <button
              key={g.id}
              type="button"
              role="radio"
              aria-checked={activo}
              onClick={() => setGranularidad(g.id as Granularidad)}
              className={cn(
                "h-6 rounded-[3px] px-2 text-xs font-medium transition-colors duration-100",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                activo
                  ? "bg-surface-sunken text-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {g.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function FinanceToolbar() {
  const { anio, setAnio } = useFinanceFilters()
  return (
    <Toolbar>
      <ToolbarField label={t("Año")} htmlFor="anio-finanzas">
        <Select value={String(anio)} onValueChange={(v) => setAnio(Number(v))}>
          <SelectTrigger id="anio-finanzas" className="w-28"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ANIOS_DISPONIBLES.map((y) => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ToolbarField>

      {/*
        EL ALCANCE, COMO RÓTULO Y NO COMO FILTRO.

        Aquí había un desplegable de país que arrancaba en «Consolidado». Eran
        dos mandos para la misma pregunta —éste y el selector de sociedad de la
        barra de arriba— y se contradecían: arriba ponía «SIETE AGENCY MÉXICO» y
        la tabla enseñaba la suma de los cuatro países. Ver `lib/alcance.ts`.

        Ahora Finanzas es SIEMPRE de una sociedad y esto sólo dice cuál. Se
        muestra igualmente —no se esconde por ser un dato y no una elección—
        porque mirar la sociedad equivocada es el error caro de un producto
        contable, y la única defensa barata es tenerlo delante.
      */}
      <AlcanceDeFinanzas />

      <div className="hidden self-stretch border-l md:block" aria-hidden="true" />

      <GranularidadSwitch />
    </Toolbar>
  )
}

/**
 * QUÉ SOCIEDAD SE ESTÁ MIRANDO, dicho en la barra de filtros.
 *
 * Tres estados y los tres se dicen distinto:
 *
 *   - hay sociedad y es la única de su país → el caso normal: nombre y país.
 *   - hay sociedad y COMPARTE país con otra → las cifras incluyen las dos y hay
 *     que decirlo. `financial_fact` se lleva por país, no por sociedad, así que
 *     el filtro no las separa. Ver la nota larga de `lib/alcance.ts`.
 *   - no hay sociedad elegida → **son TODAS**, y se dice con esas palabras.
 *
 * El tercero cambió. Decía «Elige una sociedad arriba» en ámbar y no consultaba
 * nada, mientras las tarjetas de la misma pantalla ya mostraban el consolidado:
 * la tabla salía vacía diciendo «no hay estado de resultados» cuando lo que
 * pasaba es que nunca lo pidió. Un aviso en ámbar para un estado que es
 * perfectamente válido —mirar el grupo— además lo hace parecer un error.
 *
 * Sigue sin ser un desplegable: el mando es el selector de arriba, y tener dos
 * para la misma pregunta fue el defecto que esta barra existe para no repetir.
 * Esto es un RÓTULO que dice de quién son las cifras.
 */
function AlcanceDeFinanzas() {
  const { sociedad, pais, comparten } = useFinanceFilters()

  if (!sociedad || !pais) {
    return (
      <ToolbarField label={t("Sociedad")}>
        <span
          className="inline-flex h-8 items-center rounded-md border bg-surface-sunken px-2 text-xs font-medium"
          title={t("Las cifras son la suma de todas tus sociedades, en USD. Para el consolidado de verdad —eliminando lo que se facturan entre ellas— está Holding.")}
        >
          {t("Todas las sociedades")}
        </span>
      </ToolbarField>
    )
  }

  return (
    <ToolbarField label={t("Sociedad")}>
      <span className="inline-flex h-8 min-w-0 items-center gap-1.5 rounded-md border bg-surface-sunken px-2">
        <span className="truncate text-xs font-medium">{sociedad.name}</span>
        <span className="code shrink-0 text-2xs text-muted-foreground">{nombrePais(pais)}</span>
      </span>
      {comparten.length > 0 ? (
        <span
          className="ml-1.5 inline-flex h-8 items-center rounded-md border border-warning/50 bg-warning-soft px-2 text-2xs text-warning-foreground"
          title={`El modelo financiero se lleva por país, no por sociedad: estas cifras incluyen también ${comparten
            .map((e) => e.name)
            .join(", ")}.`}
        >
          {t("incluye")}{" "}{comparten.length + 1}{" "}{t("sociedades de")}{" "}{nombrePais(pais)}
        </span>
      ) : null}
    </ToolbarField>
  )
}

/** La barra de filtros, salvo donde no filtraría nada. */
function Filtros() {
  const { pathname } = useLocation()
  const vista = pathname.split("/").filter(Boolean).pop() ?? ""
  if (SIN_FILTROS.includes(vista)) return null
  return <FinanceToolbar />
}

export function FinanceLayout() {
  return (
    <FinanceFiltersProvider>
      <div className="space-y-4">
        <PageHeader
          eyebrow="Finanzas"
          title={t("Modelo de gestión")}
          description={t("P&L, caja y sus componentes, en USD y consolidando Perú, México, Brasil y Panamá. Real y proyectado. No es el libro contable: es lo que la empresa usa para decidir.")}
        />
        <Tabs />
        <Filtros />
        <Outlet />
      </div>
    </FinanceFiltersProvider>
  )
}
